import React from "react";
import { useHistory } from "react-router-dom";
import useFetch from "hooks/use-fetch";
import apiRequest from "utils/api";
import { NewOrderErrorPage } from "components/new-email-order/error-page";
import { Spinner } from "components/ui";
import PropTypes from "prop-types";
import { NotificationManager } from "react-notifications";

export const OrderFormContext = React.createContext({});

const OrderFormProvider = ({
  patientId,
  token,
  origin,
  productLine: initialProductLine,
  children,
  handleCreateOrderSuccess
}) => {
  // Accept productLine from location.state if present
  const location = typeof window !== 'undefined' ? require('react-router-dom').useLocation() : { state: {} };
  const productLineFromState = location.state?.nextProductLine;
  const [selectedProductLine, setSelectedProductLine] = React.useState(productLineFromState || initialProductLine || "");
  const orderFormInfoAPI = useFetch({
    apiFn: ({ patientId, productLine }) =>
      apiRequest("patient_portal/mobile/order_form", {
        method: "POST",
        body: { patientId, productLine },
        headers: {
          authorization: `Bearer ${token}`
        }
      }),
    defaultValue: {
      questions: [],
      last_order: {},
      supply_options: [],
      supply_period_options: []
    }
  });

  const patientFormAttributesAPI = useFetch({
    apiFn: () =>
      apiRequest(`patient_portal/mobile/form_attributes`, {
        method: "GET",
        headers: {
          authorization: `Bearer ${token}`
        }
      }),
    defaultValue: {
      insurance_companies: [],
      guarantor_options: []
      //company_order_settings: {}
    }
  });

  const patientProfileAPI = useFetch({
    apiFn: patientId =>
      apiRequest(`patient_portal/mobile/${patientId}/info`, {
        method: "GET",
        headers: {
          authorization: `Bearer ${token}`
        }
      }),
    defaultValue: {}
  });

  const patientHCPCHistoryAPI = useFetch({
    apiFn: patientId =>
      apiRequest(`patient_portal/mobile/${patientId}/hcpcs-ordered`, {
        method: "GET",
        headers: {
          authorization: `Bearer ${token}`
        }
      }),
    defaultValue: { eligibility: [], outreachDate: "" }
  });

  const history = useHistory();
  const submitOrderAPI = useFetch({
    apiFn: values =>
      apiRequest(`patient_portal/mobile/create-new-order`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`
        },
        body: { ...values }
      }),
    onSuccess: msg => {
      NotificationManager.success("Your Order has been submitted");
      // Multi-product-line logic: accumulate all ordered product lines in sessionStorage
      // so the thank-you page can filter them all out across multiple orders in one session.
      // Cleared on logout (see actions/auth.js logoutUser).
      const storageKey = "s3_ordered_product_lines";
      let existing;
      try {
        existing = JSON.parse(sessionStorage.getItem(storageKey) || "[]");
      } catch {
        existing = [];
      }
      const updatedOrdered = [...new Set([...existing, selectedProductLine])];
      sessionStorage.setItem(storageKey, JSON.stringify(updatedOrdered));

      const allProductLines = patientProfileAPI?.response?.patient_product_lines || [];
      const currentlyOrderedProductLine = selectedProductLine;

      history.push({
        pathname: "/thanks",
        state: {
          message: "Your Order has been submitted",
          allProductLines,
          currentlyOrderedProductLine,
          patientHCPCHistory: patientHCPCHistoryAPI.response?.eligiblity,
          origin: origin,
          token: token
        }
      });
    },
    onError: err => NotificationManager.error(err.message, "Order Error")
  });

  const incompleteOrderTrackingAPI = useFetch({
    apiFn: values =>
      apiRequest(`patient_portal/mobile/incomplete-order-tracking`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`
        },
        body: { ...values }
      }),
    onError: err => NotificationManager.error(err.message, "Incomplete Order Tracking Error")
  });

  React.useEffect(() => {
    if (selectedProductLine) {
      orderFormInfoAPI.fetchData({
        patientId,
        productLine: selectedProductLine
      });
    }
  }, [token, selectedProductLine]);

  React.useEffect(() => {
    patientHCPCHistoryAPI.fetchData(patientId);
    patientFormAttributesAPI.fetchData(patientId);
    patientProfileAPI.fetchData(patientId);
  }, [token]);

  if (
    orderFormInfoAPI.isFetching ||
    patientFormAttributesAPI.isFetching ||
    patientHCPCHistoryAPI.isFetching ||
    patientProfileAPI.isFetching
  ) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          minHeight: "100vh",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Spinner />
      </div>
    );
  }

  if (
    orderFormInfoAPI.error ||
    patientFormAttributesAPI.error ||
    patientHCPCHistoryAPI.error ||
    patientProfileAPI.error
  ) {
    return <NewOrderErrorPage />;
  }

  const { questions, supply_options, supply_period_options, last_order } =
    orderFormInfoAPI.response;


  const eligibleSupplies =
    patientHCPCHistoryAPI?.response?.eligiblity
      ?.filter(
        x =>
        (x.isEligible || x.isSoonEligible) &&
          x.productType &&
          supply_options.includes(x.productType.toLowerCase())
      )
      .map(x => x.productType.toLowerCase()) ?? [];

  const { insurance_companies, guarantor_options } =
    patientFormAttributesAPI.response;

  const supplyPeriodOptions = (supply_period_options ?? []).map(x => ({
    text: `${x} Day Supply`,
    value: String(x)
  }));

  return (
    <OrderFormContext.Provider
      value={{
        productLine: selectedProductLine,
        setProductLine: setSelectedProductLine,
        patientId,
        patientInfo: patientProfileAPI?.response,
        patientHCPCHistory: patientHCPCHistoryAPI.response?.eligiblity,
        questions,
        supply_options,
        eligibleSupplies,
        supplyPeriodOptions,
        last_order,
        insurance_companies,
        guarantor_options,
        order_create_method: origin,
        submitOrderAPI,
        incompleteOrderTrackingAPI
      }}
    >
      {children}
    </OrderFormContext.Provider>
  );
};

OrderFormProvider.propTypes = {
  patientId: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  origin: PropTypes.string,
  productLine: PropTypes.string,
  children: PropTypes.node,
  handleCreateOrderSuccess: PropTypes.func
};

export default OrderFormProvider;
